import {BrowserModule} from '@angular/platform-browser';
import {ErrorHandler, NgModule, Injectable} from '@angular/core';
import {AppRoutingModule} from './app-routing.module';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {HttpClient, HttpClientModule} from '@angular/common/http';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {AppComponent} from './app.component';
import {MainLayoutModule} from './components/smart/main-layout/main-layout.module';
import {GlobalServicesModule} from './common/interceptors/global-services.module';
import {StoreModule} from '@ngrx/store';
import {buttonSettingsReducer} from './common/ngrx-store/reducers/button-settings.reducer';
import {settingServicesReducer} from './common/ngrx-store/reducers/user-services.reducer';
import {settingGlobalServicesReducer} from './common/ngrx-store/reducers/user-global-services.reducer';
import {paymentMethodReducer} from './common/ngrx-store/reducers/payment-method.reducer';
import {globalLoadingReducer} from './common/ngrx-store/reducers/global-loading.reducer';
import {breadcrumbReducer} from './common/ngrx-store/reducers/breadcrumb.reducer';
import {AuthLayoutModule} from './auth/auth-layout/auth-layout.module';
import {ToastContainerDirective, ToastrModule} from 'ngx-toastr';
import {IconsModule} from './services/icons.module';
import {MAT_FORM_FIELD_DEFAULT_OPTIONS} from '@angular/material/form-field';
import {MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS} from '@angular/material/slide-toggle';
import {MAT_LEGACY_SNACK_BAR_DEFAULT_OPTIONS as MAT_SNACK_BAR_DEFAULT_OPTIONS} from '@angular/material/legacy-snack-bar';
import {SNACKBAR_DURATION} from './common/utils/constants';
import {MAT_SELECT_CONFIG} from '@angular/material/select';
import {MAT_RIPPLE_GLOBAL_OPTIONS} from '@angular/material/core';
import {GoogleTagManagerModule, GoogleTagManagerService} from 'angular-google-tag-manager';
import {IntercomModule} from '@supy-io/ngx-intercom';
import {OPENED_TOASTS_LIMIT, TOAST_MESSAGE_TIMEOUT} from './types/toastr.type';
import {environment} from '../environments/environment';
import {AirbrakeErrorHandler} from './common/utils/airbrake-error-handler';
import {CustomToastComponent} from './components/dumb/custom-toast/custom-toast.component';
import {CustomGTMService} from './services/gtm.service';

const ProductionProviders = environment.production ? [{provide: ErrorHandler, useClass: AirbrakeErrorHandler}] : [];

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    GlobalServicesModule,
    StoreModule.forRoot({
      buttonSettings: buttonSettingsReducer,
      settingServices: settingServicesReducer,
      globalSettingServices: settingGlobalServicesReducer,
      paymentMethod: paymentMethodReducer,
      globalLoading: globalLoadingReducer,
      breadcrumb: breadcrumbReducer,
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'en',
    }),
    ToastrModule.forRoot({
      positionClass: 'inline',
      closeButton: true,
      tapToDismiss: false,
      disableTimeOut: false,
      autoDismiss: true,
      easeTime: 150,
      timeOut: TOAST_MESSAGE_TIMEOUT,
      maxOpened: OPENED_TOASTS_LIMIT,
      toastComponent: CustomToastComponent,
    }),
    ToastContainerDirective,
    MainLayoutModule,
    AuthLayoutModule,
    IconsModule,
    GoogleTagManagerModule.forRoot({
      id: environment.googleTagManagerId,
    }),
    IntercomModule.forRoot({
      appId: environment.intercomId,
      updateOnRouterChange: true,
    }),
  ],
  providers: [
    {provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: {appearance: 'outline', floatLabel: 'auto'}},
    {
      provide: MAT_SLIDE_TOGGLE_DEFAULT_OPTIONS,
      useValue: {
        color: 'primary',
        hideIcon: true,
      },
    },
    {provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: {duration: SNACKBAR_DURATION}},
    {
      provide: MAT_SELECT_CONFIG,
      useValue: {
        disableOptionCentering: true,
        overlayPanelClass: 'mat-select-overlay',
      },
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: {disabled: true},
    },
    {
      provide: GoogleTagManagerService,
      useClass: CustomGTMService,
    },
    ...ProductionProviders,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
